import {
  ConnectionStates,
  QualityLevel,
  StreamStates,
  avSelectors,
  useAvContext,
} from "@minervaproject/avsubsystem";
import { LoadingCircleIcon, ProfilePhoto } from "@minervaproject/fds";
import clsx from "clsx";
import { useEffect, useRef } from "react";
import { shallowEqual } from "react-redux";

import { useAppSelector } from "hooks/redux_hooks";
import { PROFILE_PROPERTIES, useUserProperties } from "hooks/use_user_properties";
import { SizeType } from "shared/types";
import { classroomSelectors, localSelectors } from "store/index";
import { computeInitials, profilePhotoUrl } from "utils/user";

import "./participant_av.scss";

const SIZE_MAP: Record<SizeType | number, number> = {
  xs: 0, // not used here
  sm: 24,
  md: 32,
  lg: 48,
  xl: 100,
};

const STATE_NEEDS_ACTION = [StreamStates.initial, StreamStates.failed];

function LoadingCircle({ size = "xl" }: { size: SizeType }) {
  return (
    <LoadingCircleIcon className="absolute-center" width={SIZE_MAP[size]} height={SIZE_MAP[size]} />
  );
}

export type ParticipantVideoProps = {
  userId: string;
  isSelf: boolean;
  isSquircle?: boolean;
  includeAudio?: boolean;
  includeVideo?: boolean;
  isParticipantHidden?: boolean;
  profilePhotoSize?: SizeType;
};

function ParticipantAv({
  userId,
  isSelf,
  isSquircle = false,
  includeAudio = true,
  includeVideo = true,
  isParticipantHidden = false,
  profilePhotoSize = "xl",
}: ParticipantVideoProps) {
  const user = useUserProperties(
    userId,
    PROFILE_PROPERTIES.concat("is_performing_av_check", "is_present", "is_speaking"),
  );

  const connectionReady = avSelectors.useConnectionState() === ConnectionStates.connected;
  const streamState = avSelectors.useStreamState(userId);
  const isSpeaking = user?.is_speaking;
  const isCopy = !includeAudio;
  const videoElement = useRef<HTMLDivElement>(null);
  const avConnection = useAvContext();
  const currentUserId = useAppSelector(localSelectors.selectCurrentUserId());
  const currentUserIsPerformingAvCheck = useUserProperties(currentUserId, [
    "is_performing_av_check",
  ])?.is_performing_av_check;

  const [shouldMuteAudio, reason] = useAppSelector(
    classroomSelectors.selectShouldMuteUserAudio(userId),
    shallowEqual,
  );

  useEffect(() => {
    if (shouldMuteAudio) {
      window.flightLogger.log(`[ParticipantAv] Muting audio for ${userId}: ${reason}`);
    } else {
      window.flightLogger.log(`[ParticipantAv] Unmuting audio for ${userId}: ${reason}`);
    }
  }, [shouldMuteAudio, reason, userId]);

  const isPerformingAvCheck = user?.is_performing_av_check;
  const isReady = streamState?.state === StreamStates.playable;
  const isNotLoading =
    isReady ||
    !streamState ||
    streamState.state === StreamStates.failed ||
    streamState.state === StreamStates.initial;

  //  Should request a publish if it's not a copy, the user is out of av check and is not published or in transition
  //  we will also publish again if the video element is not present - the av subsytem will create a new one
  const shouldRequestLocalVideo =
    isSelf && !isCopy && isPerformingAvCheck === false && !videoElement.current?.id && isReady;
  //  Should subscribe if it is a remote video and it is not subscribed or in transition
  const shouldSubscribe =
    !isSelf &&
    !isCopy &&
    currentUserIsPerformingAvCheck === false &&
    (!streamState || STATE_NEEDS_ACTION.includes(streamState.state));

  const shouldRequestCopy = isCopy && isReady;

  // A video is enabled if locally we have requested the video feed
  const isVideoEnabled = avSelectors.useStreamVideoEnabled(userId);
  // A video is available if there is a video track to play
  const isVideoAvailable = avSelectors.useStreamVideoAvailable(userId);

  const hideVideo = !isReady || !isVideoEnabled || !isVideoAvailable;

  // Handle subscribe, copy, publish and unpublish (for A/V preferences)
  useEffect(() => {
    if (!avConnection || !connectionReady || !videoElement?.current) {
      window.flightLogger.log(`[ParticipantAv] No action to perform yet userId: ${userId}`);
    } else if (shouldRequestCopy) {
      window.flightLogger.log(`[ParticipantAv] Requesting copy for ${userId}`);
      videoElement.current.id = avConnection.showVideoCopy(userId);
      if (!isSelf) {
        window.flightLogger.log(
          `[ParticipantAv] Setting copy quality level for subscription: ${userId}`,
        );
        avConnection.setQualityLevelForRemoteStream(userId, QualityLevel.High);
      }
    } else if (shouldRequestLocalVideo) {
      window.flightLogger.log(`[ParticipantAv] Requesting local video (publish) for ${userId}`);
      videoElement.current.id = avConnection.publish(true, true);
    } else if (shouldSubscribe) {
      window.flightLogger.log(
        `[ParticipantAv] Requesting subscribe for ${userId} and setting normal subscribe quality`,
      );
      videoElement.current.id = avConnection.subscribe(userId, includeVideo);
      avConnection.setQualityLevelForRemoteStream(userId, QualityLevel.LowFullMotion);
    }
  }, [
    avConnection,
    isSelf,
    userId,
    shouldRequestCopy,
    includeVideo,
    connectionReady,
    shouldRequestLocalVideo,
    shouldSubscribe,
  ]);

  // When the component unmounts, we will unpublish, unsubscribe or remove the copy
  useEffect(() => {
    return () => {
      if (!avConnection) {
        window.flightLogger.log(
          `[ParticipantAv] useEffect cleanup: No avConnection present userId: ${userId}`,
        );
        return;
      }
      if (!isCopy) {
        // We unsubscribe from removed elements - we don't unpublish as an optimization
        if (!isSelf) {
          window.flightLogger.log(
            `[ParticipantAv] useEffect cleanup: Will request unsubscribe for ${userId}`,
          );
          avConnection.unsubscribe(userId);
        }
      } else {
        window.flightLogger.log(
          `[ParticipantAv] useEffect cleanup: Will request remove copy, userId:${userId}`,
        );
        avConnection.removeVideoCopy(userId);
        if (!isSelf) {
          window.flightLogger.log(
            `[ParticipantAv] useEffect cleanup: Setting quality, userId:${userId}`,
          );
          avConnection.setQualityLevelForRemoteStream(userId, QualityLevel.LowFullMotion);
        }
      }
    };
  }, [avConnection, isSelf, isCopy, userId]);

  // mute remote audio if the local user is in av check
  useEffect(() => {
    if (!isSelf && isReady) {
      avConnection?.setRemoteAudio(userId, !shouldMuteAudio);
    }
  }, [shouldMuteAudio, isSelf, isReady, avConnection, userId]);

  if (!user) {
    return null;
  }

  return (
    <div
      className={clsx("participant-video bg-black-tint-10", {
        "is-self": isSelf,
        "is-speaking": isSpeaking && includeVideo,
        "squircle-video": isSquircle,
      })}
      data-testid="participant-video"
    >
      <div className="participant-video-container" ref={videoElement} hidden={!isNotLoading} />
      {!isParticipantHidden ? (
        <>
          {hideVideo ? (
            <ProfilePhoto
              className={clsx("absolute-center", { "is-speaking": isSpeaking && !includeVideo })}
              size={profilePhotoSize}
              src={profilePhotoUrl(user)}
              initials={computeInitials(user)}
              style={{ opacity: user.is_present ? 1 : 0.5 }}
            />
          ) : null}
          {!isNotLoading && user.is_present ? <LoadingCircle size={profilePhotoSize} /> : null}
          <div className="focus-outline" />
        </>
      ) : null}
    </div>
  );
}

export { ParticipantAv };
